import { FormGroup, FormParagraph, FormSection, IconButton } from '@ksyos/design-system';
import type { SettingName, SettingType } from '@common/utils/settings';
import type { IServiceEmployment } from '@frontend/components/interfaces';
import { SettingsSwitch } from '@frontend/components/pages/carePath/modals/components/SettingsSwitch';
import { translateKey } from '@frontend/translate';
import type {
    IServiceEmploymentSetting,
    IServiceEmploymentSettingList,
} from '../../addRights/utils/serviceEmploymentSettingsUtils';

interface ServiceEmploymentSettingListProps {
    mode: 'read' | 'edit';
    onChange: (updatedSettingList: IServiceEmployment, deletedActivity?: string) => void;
    serviceEmploymentSettingsList: IServiceEmploymentSettingList;
    serviceRight: IServiceEmployment;
}

export function ServiceEmploymentSettingList(props: ServiceEmploymentSettingListProps) {
    const { mode, serviceEmploymentSettingsList, serviceRight, onChange } = props;

    return <>{renderFormSections()}</>;

    function renderFormSections() {
        const settingSectionHeaders = [
            ...new Set(Object.entries(serviceEmploymentSettingsList).map(([_, item]) => item.header)),
        ];

        return settingSectionHeaders.map((settingSection) => {
            const settingsPerSection = Object.entries(serviceEmploymentSettingsList).filter(
                ([, item]) => item.header === settingSection
            );

            return (
                <FormSection
                    key={`settingSection-${settingSection}`}
                    title={settingSection}
                >
                    {settingsPerSection.map(([settingName, settingProps]) => {
                        const value = findSettingValue(settingName as SettingName);

                        return (
                            <FormGroup
                                key={`settingLabel-${settingProps.label}`}
                                label={settingProps.label}
                            >
                                {renderSetting(settingName, settingProps, mode === 'edit', value)}
                            </FormGroup>
                        );
                    })}
                </FormSection>
            );
        });
    }

    function findSettingValue(settingName: SettingName) {
        if (settingName in serviceRight.settings) {
            return serviceRight.settings[settingName];
        } else if (settingName === 'canStartProcess' || settingName === 'isRequester') {
            return serviceRight[settingName];
        }

        return undefined;
    }

    function renderSetting(
        settingName: string,
        settingProps: IServiceEmploymentSetting,
        isEditMode: boolean,
        settingValue?: SettingType
    ) {
        switch (settingProps.header) {
            case translateKey('addCarePathSettingsHeaders.activities'):
                return isEditMode ? (
                    <IconButton
                        icon="delete"
                        onClick={() => deleteActivity(settingName)}
                        size="small"
                    />
                ) : null;

            default:
                return isEditMode ? (
                    <SettingsSwitch
                        listKey={settingName}
                        onChange={(value) => onHandleChange(settingName, value)}
                        value={settingValue as boolean | undefined}
                    />
                ) : (
                    <FormParagraph>
                        {settingValue ? translateKey('common.yes') : translateKey('common.no')}
                    </FormParagraph>
                );
        }
    }

    function onHandleChange(settingName: string, value: SettingType) {
        let updatedServiceRight = {
            ...serviceRight,
        };

        updatedServiceRight = {
            ...updatedServiceRight,
            settings: {
                ...updatedServiceRight.settings,
                [settingName]: value,
            },
        };

        onChange(updatedServiceRight);
    }

    function deleteActivity(activityName: string) {
        const activityId = activityName.replace('activity_', '');

        const updatedServiceRightActivityPerformers = serviceRight.serviceActivityAuthorizations.filter(
            (serviceActivityAuthorization) => serviceActivityAuthorization.serviceActivity.id !== activityId
        );

        const updatedServiceRight = {
            ...serviceRight,
            serviceActivityAuthorizations: updatedServiceRightActivityPerformers,
        };

        onChange(updatedServiceRight, activityName);
    }
}
